import clsx from 'clsx'
import { Helper, Icon, Typography } from 'components'
import type { Dayjs } from 'dayjs'
import { DateTimerProps, TypographyTypes } from 'interfaces'
import dayjs from 'lib/dayjs'
import React, { forwardRef, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const toDate = (val: string | Dayjs | Date | undefined): Date | null => {
	if (!val) return null
	if (val instanceof Date) return val
	if (typeof val === 'object' && 'toDate' in val) return (val as Dayjs).toDate()
	const d = dayjs(val as string)
	return d.isValid() ? d.toDate() : null
}

interface DateTimerInputProps {
	value?: string
	onClick?: () => void
	placeholder?: string
	icon: string
	line: boolean
	disabled?: boolean
}

const DateTimerInput = forwardRef<HTMLButtonElement, DateTimerInputProps>(
	({ value, onClick, placeholder, icon, line, disabled }, ref) => (
		<button
			type="button"
			onClick={onClick}
			ref={ref}
			disabled={disabled}
			className={clsx('w-full', line ? 'line' : 'full')}
		>
			<input
				value={value || ''}
				placeholder={placeholder}
				readOnly
				className={clsx(
					'form-control',
					'w-full',
					'outline-none',
					!line && 'rounded-lg'
				)}
			/>
			<Icon
				src={icon}
				className={clsx(
					'w-4 absolute top-0 mb-10',
					line ? 'right-0' : 'right-2'
				)}
			/>
		</button>
	)
)

DateTimerInput.defaultProps = {
	value: '',
	onClick: undefined,
	placeholder: '',
	disabled: false
}

interface TimeInputProps {
	value?: string
	onChange?: (time: string) => void
}

const TimeInput: React.FC<TimeInputProps> = ({ value = '', onChange }) => {
	const parseTime = (
		v: string
	): { h: number; m: number; period: 'AM' | 'PM' } => {
		const [hourStr = '0', minStr = '0'] = v.split(':')
		const h24 = parseInt(hourStr, 10) || 0
		const m = parseInt(minStr, 10) || 0
		const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM'
		const h = h24 % 12 || 12
		return { h, m, period }
	}

	const { h, m, period } = parseTime(value)

	const emit = (newH: number, newM: number, newPeriod: 'AM' | 'PM'): void => {
		const h24 =
			newPeriod === 'PM'
				? newH === 12
					? 12
					: newH + 12
				: newH === 12
				? 0
				: newH
		onChange?.(
			`${String(h24).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
		)
	}

	const stopClose = (e: React.MouseEvent): void => e.preventDefault()

	const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const raw = e.target.value.replace(/\D/g, '')
		if (!raw) return
		const val = Math.min(12, Math.max(1, parseInt(raw, 10)))
		emit(val, m, period)
	}

	const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const raw = e.target.value.replace(/\D/g, '')
		if (!raw) return
		const val = Math.min(59, Math.max(0, parseInt(raw, 10)))
		emit(h, val, period)
	}

	const inputClass =
		'w-10 text-center text-sm font-medium border border-gray-200 rounded py-0.5 focus:outline-none focus:border-blue-1 time-spinner-input'

	return (
		<div className="flex items-center justify-center gap-2 py-2 border-t border-gray-100">
			<input
				type="number"
				min={1}
				max={12}
				value={String(h).padStart(2, '0')}
				onChange={handleHourChange}
				className={inputClass}
			/>
			<span className="font-medium text-sm">:</span>
			<input
				type="number"
				min={0}
				max={59}
				value={String(m).padStart(2, '0')}
				onChange={handleMinuteChange}
				className={inputClass}
			/>
			<button
				type="button"
				onMouseDown={stopClose}
				onClick={() => emit(h, m, period === 'AM' ? 'PM' : 'AM')}
				className="text-sm font-medium w-10 text-center border border-gray-200 rounded py-0.5 hover:bg-gray-50"
			>
				{period}
			</button>
		</div>
	)
}

TimeInput.defaultProps = {
	value: '',
	onChange: undefined
}

export const DateTimer: React.FC<DateTimerProps> = ({
	icon,
	title,
	setDate,
	validate,
	validateMax,
	className,
	line = true,
	initialValue,
	disabled = false,
	placeholderInput,
	timeFormat = 'hh:mm A',
	helper,
	textHelper,
	hasDatesBefore = false,
	value: dateTimeValue,
	autoInitialize = false,
	portalId,
	popperClassName
}): JSX.Element => {
	const selected =
		toDate(dateTimeValue) ??
		toDate(initialValue) ??
		(autoInitialize ? dayjs().add(5, 'minutes').toDate() : null)

	const handleChange = (date: Date | null): void => {
		if (date) setDate(dayjs(date).toISOString())
	}

	useEffect(() => {
		if (autoInitialize && !initialValue && !dateTimeValue) {
			setDate(dayjs().add(5, 'minutes').toISOString())
		}
	}, [initialValue, dateTimeValue, autoInitialize])

	const showTime = timeFormat !== false

	return (
		<div className={className}>
			{title && !helper && (
				<Typography
					title={title}
					type={TypographyTypes.span}
					fontLeading="12/12"
					className="text-black-1"
				/>
			)}
			{title && helper && textHelper && (
				<div className="w-full flex justify-between items-center mb-3 ">
					<Typography
						title={title}
						type={TypographyTypes.span}
						fontLeading="12/12"
						className="text-black-1"
					/>
					<Helper text={textHelper} />
				</div>
			)}
			<div className="relative w-full">
				<DatePicker
					selected={selected}
					onChange={handleChange}
					showTimeInput={showTime}
					timeInputLabel=""
					shouldCloseOnSelect={!showTime}
					customTimeInput={showTime ? <TimeInput /> : undefined}
					dateFormat={showTime ? 'MM/dd/yyyy hh:mm aa' : 'MM/dd/yyyy'}
					minDate={!hasDatesBefore ? toDate(validate) : undefined}
					maxDate={validateMax ? toDate(validateMax) : undefined}
					disabled={disabled}
					placeholderText={placeholderInput || ''}
					wrapperClassName="w-full"
					customInput={
						<DateTimerInput
							icon={icon}
							line={line}
							disabled={disabled}
							placeholder={placeholderInput || ''}
						/>
					}
					popperPlacement="bottom-start"
					portalId={portalId}
					popperClassName={popperClassName}
				/>
			</div>
		</div>
	)
}
